import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {SectionItem} from '../../../interfaces/form/section-item';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {LayoutItem} from '../../../interfaces/form/layout-item';
import {FieldItem} from '../../../interfaces/form/field-item';
import {Form} from '../../../interfaces/entities/form';
import {Field} from '../../../interfaces';
import {startWith, takeUntil} from 'rxjs/operators';
import {FieldHelperService} from '@app/services/util/field-helper.service';
import {Subject} from 'rxjs';
import {FieldTypeEnum} from '@app/enums/FieldTypeEnum';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {INTEGRATION_SETTINGS_ROUTES, IntegrationsNames} from '@app/enums/IntegrationTypeEnum';
import {FormSectionExpandService} from '@app/services/util/form-section-expand.service';
import {COST_REPEATERS} from '@app/shared/v2/v2-costs-card/v2-costs-card.component';


@Component({
    selector: 'app-form-layout',
    templateUrl: './form-layout.component.html',
    styleUrls: ['./form-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormLayoutComponent implements OnInit, OnDestroy {

  @ViewChildren('inputElement') inputElements: QueryList<ElementRef>;
  @Input() form: Form;

  @Input() sectionItem: SectionItem;

  @Input() formGroup: FormGroup;

  @Input('hidden') isHidden = false;

  @Input() hideHeader = true;
  @Input() disabled = false;
  @Input() showBooleanDescription = false;
  /** Set by the vehicle forms so a portal section's settings popup can resolve
   *  this vehicle's branch. Null on the create form (BRIEFD-7086). */
  @Input() vehicleUuid: string | null = null;
  /** v2 vehicle form: render the expenses repeater as the Design 2.0 costs
   *  card instead of the inline legacy repeater (BRIEFD-7558). */
  @Input() costsCard = false;
  /** Fires when a settings popup saved something, so the form showing these
   *  values can refresh instead of going stale (BRIEFD-7086). */
  @Output() settingsSaved = new EventEmitter<void>();

  /**
   * Local expand/collapse state for collapsible sections. Only used when
   * `sectionItem.collapsible === true`; non-collapsible sections ignore
   * this entirely and always render their body.
   */
  public isExpanded: boolean = true;

  /** `*ngFor` identity for `sectionItem.items` — position, not object. */
  public trackByIndex(index: number): number {
    return index;
  }

  /** Non-null when this section belongs to one advertising portal — the
   *  per-integration advertising subsections carry their integration key, which
   *  earns them a settings link in the header (BRIEFD-7086). */
  public get portalSettingsKey(): string | null {
    const key = this.sectionItem?.key;
    return key && Object.prototype.hasOwnProperty.call(IntegrationsNames, key) ? key : null;
  }

  /** True for the advertising content section — its settings are the company
   *  description that gets appended to every ad. */
  public get hasDescriptionSettings(): boolean {
    return this.sectionItem?.key === 'advertising_content';
  }

  /** Any settings popup available for this section at all. */
  public get hasSectionSettings(): boolean {
    return !!this.portalSettingsKey || this.hasDescriptionSettings;
  }

  /** Open this section's settings popup. Stops the click from also toggling the
   *  section, whose header carries its own (click) handler.
   *
   * The dialogs are imported dynamically on purpose: they reuse the settings
   * page's own forms, which pull in SharedModule — and SharedModule pulls in
   * this module, so a static import is a cycle that leaves the app stuck on its
   * loading spinner ("Cannot access 'SharedModule' before initialization").
   */
  public async openSectionSettings(event: Event): Promise<void> {
    event.stopPropagation();
    const key = this.portalSettingsKey;
    if (key && INTEGRATION_SETTINGS_ROUTES[key]) {
      // Virtual Salon has its own settings page — the ad-feed popup has nothing
      // to show for it (BRIEFD-7094).
      this.router.navigate([INTEGRATION_SETTINGS_ROUTES[key]]);

      return;
    }
    if (key) {
      const {PortalSettingsDialogComponent} =
        await import('@app/shared/dialogs/portal-settings-dialog/portal-settings-dialog.component');
      PortalSettingsDialogComponent.open(this.dialog, key, this.vehicleUuid)
        .afterClosed()
        .subscribe((saved) => { if (saved) { this.settingsSaved.emit(); } });
      return;
    }
    if (this.hasDescriptionSettings) {
      const {AdvertisingDescriptionSettingsDialogComponent} =
        await import('@app/shared/dialogs/advertising-description-settings-dialog/advertising-description-settings-dialog.component');
      AdvertisingDescriptionSettingsDialogComponent.open(this.dialog)
        .afterClosed()
        .subscribe((saved) => { if (saved) { this.settingsSaved.emit(); } });
    }
  }

  protected destroy$ = new Subject<void>();
  protected readonly FieldTypeEnum = FieldTypeEnum;

  constructor(
    private fieldHelper: FieldHelperService,
    private dialog: MatDialog,
    private sectionExpand: FormSectionExpandService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.setRequiredFields();
    if (this.sectionItem?.collapsible) {
      this.isExpanded = this.sectionItem.defaultOpen ?? true;
      // Rendered after the jump was requested (the advertising subsections build
      // asynchronously) — open straight away rather than starting collapsed.
      if (!this.isExpanded && this.hasPendingField(this.sectionItem)) {
        this.isExpanded = true;
      }
    }

    // Open on demand when the page jumps to a field buried in this section —
    // otherwise the field isn't rendered at all and the jump silently does
    // nothing (BRIEFD-7094).
    this.sectionExpand.expandRequests
      .pipe(takeUntil(this.destroy$))
      .subscribe((fieldUuid) => {
        if (!this.isExpanded && this.containsField(this.sectionItem, fieldUuid)) {
          this.isExpanded = true;
          this.cdr.markForCheck();
        }
      });
  }

  /** Whether any field in this section is waiting to be jumped to. */
  private hasPendingField(section: SectionItem): boolean {
    return (section?.items ?? []).some((item: any) => item?.type === 'field'
      ? this.sectionExpand.isPending(item.fieldUuid)
      : this.hasPendingField(item as SectionItem));
  }

  /** Whether this section (or one nested in it) renders the given field. */
  private containsField(section: SectionItem, fieldUuid: string): boolean {
    return (section?.items ?? []).some((item: any) => item?.type === 'field'
      ? item.fieldUuid === fieldUuid
      : this.containsField(item as SectionItem, fieldUuid));
  }

  toggleSection(): void {
    if (!this.sectionItem?.collapsible) {
      return;
    }
    this.isExpanded = !this.isExpanded;
  }

  setRequiredFields() {
    const fields = this.form.fields;
    const keys = Object.keys(fields);
    for (const key of keys) {
      const config = fields[key].config;
      if (config.makesRequired) {
        const fieldsControls = this.formGroup.controls;

        // Trigger control(s): the field's own control wherever it renders —
        // a top-level control for a flat field, plus one per repeater row
        // when it lives inside one — both are supported so a single field's
        // config works either way. Confirmed via live testing that the
        // previous `getFieldControl()`-based lookup here never actually
        // attached this subscription for the Gorivo/Prostornina pair
        // (BRIEFD-7351) — this rewrite replaces that lookup entirely.
        const triggerControls: AbstractControl[] = [];
        const topLevelTrigger = fieldsControls[key];
        if (topLevelTrigger instanceof FormControl) {
          triggerControls.push(topLevelTrigger);
        }
        for (const fieldControlKey in fieldsControls) {
          const group = fieldsControls[fieldControlKey];
          if (group instanceof FormGroup && group.controls[key] instanceof FormControl) {
            triggerControls.push(group.controls[key]);
          }
        }

        for (const triggerControl of triggerControls) {
          // `startWith` replays the control's current value immediately, so
          // the rule is applied on load too — otherwise an existing vehicle
          // (fuel type already set) showed neither the requirement nor the
          // exemption until the dealer happened to re-pick it (BRIEFD-7351
          // review). `isInitialEmission` skips markAsTouched() on that
          // replayed pass — loading a form must not paint a red error before
          // the dealer has touched anything.
          let isInitialEmission = true;
          triggerControl.valueChanges
            .pipe(startWith(triggerControl.value), takeUntil(this.destroy$))
            .subscribe((valueOfField) => {
              const wasInitialEmission = isInitialEmission;
              isInitialEmission = false;

              for (const requiredFields of config.makesRequired) {
                let requiredField = null;
                for (const fieldKey in fields) {
                  if (requiredFields === fields[fieldKey].config.typeId) {
                    requiredField = fields[fieldKey].fieldUuid;
                  }
                }
                if (!requiredField) {
                  console.warn(`form-layout: makesRequired target typeId "${requiredFields}" not found in this form's fields — config/field drift?`);
                  continue;
                }

                // Same scope as the trigger: its own row's control for a
                // repeater, the top-level control otherwise.
                const parent = triggerControl.parent;
                const requiredControl = (parent instanceof FormGroup ? parent.controls[requiredField] : null)
                  ?? fieldsControls[requiredField];
                if (!requiredControl) { continue; }

                // Dropdown fields store `{key: true}` — unwrap to the plain key
                // before comparing, same as the other config.choices consumers.
                const valueKey = valueOfField && typeof valueOfField === 'object'
                  ? Object.keys(valueOfField)[0]
                  : valueOfField;

                // With `makesRequiredExceptWhen` (e.g. engine capacity required for
                // every fuel type except electric, BRIEFD-7351): required unless the
                // trigger's value is in that list. Without it (e.g. Virtual Salon's
                // leads_form_enabled toggling its form fields): required whenever the
                // trigger's own value is truthy.
                const exceptValues: string[] = config.makesRequiredExceptWhen ?? [];
                const isRequired = exceptValues.length
                  ? !exceptValues.includes(valueKey)
                  : !!valueKey;

                // addValidators/removeValidators, not setValidators([...]) —
                // setValidators replaces the WHOLE list, permanently dropping
                // any other validator (e.g. a seeded `max`) the first time
                // this fires (BRIEFD-7351 review).
                if (isRequired) {
                  requiredControl.addValidators(Validators.required);
                } else {
                  requiredControl.removeValidators(Validators.required);
                }
                requiredControl.updateValueAndValidity({emitEvent: false});
                // Only paint the error immediately when a live (not the
                // initial replayed) change just made the field required —
                // never on load, and never when the requirement is being
                // removed (that should only ever clear an error, not add one).
                if (isRequired && !wasInitialEmission) {
                  requiredControl.markAsTouched();
                }
              }
            });
        }
      }
    }
  }

  asSectionItem(item: LayoutItem) {
    return item as SectionItem;
  }

  isCostsCard(item: LayoutItem): boolean {
    return this.costsCard
      && this.form.fields[this.asFieldItem(item).fieldUuid]?.config?.typeId === COST_REPEATERS.expense.repeaterId;
  }

  costsControl(item: LayoutItem): FormArray {
    return this.formGroup.controls[this.asFieldItem(item).fieldUuid] as FormArray;
  }

  asFieldItem(item: LayoutItem) {
    return item as FieldItem;
  }

  keys(controls: any) {
    return Object.keys(controls);
  }

  fieldsUpdated(event: Array<{ fieldUuid: string, value: number }> | Field<any>) {
    if (Array.isArray(event)) {
      event.forEach((field) => {
        this.formGroup.controls[field.fieldUuid]?.setValue(field.value);
      });
    } else {
      this.formGroup.controls[event.fieldUuid].setValue(event.value);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  focusNextField(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      const target = event.target as HTMLElement;

      // If the target is the rich text editor, don't focus the next field
      if (target.classList.contains('ql-editor')) {
        return;
      }

      const inputs = document.querySelectorAll('[tabindex=\'0\']');
      const currentIndex = Array.from(inputs).findIndex((input) => input === event.target);

      if (currentIndex < inputs.length - 1) {
        const nextInput = inputs[currentIndex + 1] as HTMLElement;
        nextInput.focus();

        if (nextInput.tagName === 'BUTTON') {
          setTimeout(() => {
            const inputs = document.querySelectorAll('[tabindex=\'0\']');

            const nextInput = inputs[currentIndex + 1] as HTMLElement;
            nextInput.focus();
          }, 50);
        }
      }
    }
  }
}
